import React, { useEffect, useState } from 'react';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-data-grid-pro';
import DialogBox from "../../molecules/DialogBox/DialogBox"
import PropTypes from "prop-types";
import { NULL_DISPLAY_TEXT } from '../../atoms/NullDisplay/NullDisplay';

LicenseInfo.setLicenseKey(
  'a9d24100595887f4df6a2e3e3a05582fT1JERVI6MzkzMjMsRVhQSVJZPTE2Nzg0Mjg5NjAwMDAsS0VZVkVSU0lPTj0x',
);

/**
 * Return an MUI DataGridPro Table.
 * @param rows {object, array} Object array to build each table row item (td).
 * @param columns {object, array} Custom classes.
 * @param sortField {string} Arrays of string to set the row order on map function (strings must match the rowsData keys).
 * @param sortOrder {string}  Two values in the array to compare.
 * @param trim {boolean}  If tru the content in the cell is trimmed.
 * @returns {JSX.Element}
 */

export default function DataGridTable({
  columns,
  rows,
  sortField,
  sortOrder,
  trim = true,
  groupingModel = null,
  customStyles = [],
  getRowClassName = null,
}) {
  const [pageSize, setPageSize] = useState(10);
  const [resolution, setResolution] = useState('')

  useEffect(() => {
    setResolution(window.innerWidth < 1023 ? 'mobile' : 'desktop');
  }, [resolution])

  let rowsCount = rows ? Object.keys(rows).length : null;
  const [sortModel, setSortModel] = React.useState([
    {
      field: sortField || columns[0].field,
      sort: sortOrder || 'asc',
    },
  ]);

  const columnsCellValue = columns.map(function (obj) {
    return {
      ...obj,
      renderCell: function (params) {
        let cellVal = params.value;
        if (cellVal === null) return <span className="null-display">{NULL_DISPLAY_TEXT}</span>
        if (cellVal && cellVal.length > 40) {
           return (
            <>
              <DialogBox
                button={false}
                ctaText={params.value}
                content={params.value}
                helpText={'Click to display full text'}
                title={`${Object.values(params.row)[1]}: ${params.colDef.headerName}`} />
            </>
          );
        }
      },
    };
  });

  const handleSortModelChange = (model) => {
    const newModel = model;
    
    // If there are no options, set the default value to "asc."
    if(newModel.length === 0) {
      newModel.push({ field: columns[0].field, sort: 'asc' });
    }
  
    setSortModel(newModel);
  }

  return (
    <div data-testid="table-container" style={{ height: ((rowsCount < pageSize ? rowsCount : pageSize) * 52 + 168), width: '100%', marginTop: 20, marginBottom: 20 }}>
      <DataGridPro
        experimentalFeatures={{ columnGrouping: true }}
        columnGroupingModel={groupingModel}
        rows={rows}
        sx={{
          '& .MuiDataGrid-footerContainer': {
            borderTop: rows.length === 0 ? "none" : "1px solid rgba(224, 224, 224, 1)",
            minHeight: rows.length === 0 ? "unset" : "52px"
          },
          ...customStyles
        }}
        columns={ trim ? columnsCellValue : columns}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        checkboxSelection={false}
        disableSelectionOnClick
        density='comfortable'
        components={{
          Toolbar: GridToolbar,
        }}
        getRowClassName={getRowClassName}
        rowsPerPageOptions={[10, 20, 50, 100]}
        localeText={{
          noRowsLabel: 'No rows available',
        }}
      />
    </div>
  );
}

DataGridTable.propTypes = {
  columns: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  rows: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  sortField: PropTypes.string,
  sortOrder: PropTypes.string,
  trim: PropTypes.bool,
  getRowClassName: PropTypes.func,
}